import { useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { toast } from 'sonner';

import {
  useCreateSavedListOutreachQueue,
  type CreatorsInListDTO,
  type SavedListOutreachChannelDTO,
  type SavedListOutreachMessageDTO,
} from '@/entities/saved-creators-list';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { Switch } from '@/shared/components/shadcn/switch';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import { cn } from '@/shared/utils';
import { ArrowUpRight, Mail, MessageCircle } from 'lucide-react';
import { OutreachMessageDialog } from './outreach-message-dialog';
import { useSavedListOutreachState } from './use-saved-list-outreach-state';

type Props = {
  listId: string;
  creators: CreatorsInListDTO[];
  confirmedCreatorCount: number;
  selectedCreatorIds: string[];
  onCreateCampaign: () => void;
  onQueueCreated?: (params: {
    channel: SavedListOutreachChannelDTO;
    creatorIds: string[];
  }) => void;
  showCampaignAction?: boolean;
};

type EditingMessage = 'initial' | 'follow-up' | null;

const DEFAULT_PANEL_WIDTH = 380;
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 560;

export function SavedListOutreachPanel({
  listId,
  creators,
  confirmedCreatorCount,
  selectedCreatorIds,
  onCreateCampaign,
  onQueueCreated,
  showCampaignAction = true,
}: Props) {
  const outreach = useSavedListOutreachState({ creators, selectedCreatorIds });
  const createQueueMutation = useCreateSavedListOutreachQueue();
  const [editingMessage, setEditingMessage] = useState<EditingMessage>(null);
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(DEFAULT_PANEL_WIDTH);
  const isEmail = outreach.channel === 'email';
  const hasRecipients = outreach.recipientIds.length > 0;
  const ctaLabel = outreach.isConnected
    ? isEmail
      ? 'Start Email Outreach'
      : 'Start TikTok DM Outreach'
    : isEmail
      ? 'Connect Gmail'
      : 'Connect TikTok';

  const handlePrimaryAction = async () => {
    if (!hasRecipients) return;

    if (!outreach.isConnected) {
      outreach.connectChannel();
      toast.success(isEmail ? 'Gmail connected' : 'TikTok connected');
      return;
    }

    if (!outreach.request) return;

    const queueCreatedParams = {
      channel: outreach.request.channel,
      creatorIds: outreach.request.creator_ids,
    };

    try {
      await createQueueMutation.mutateAsync({
        listId,
        request: outreach.request,
      });
      onQueueCreated?.(queueCreatedParams);
      toast.success('Outreach started');
    } catch {
      // V1 is a mock flow, so do not expose backend/MSW gaps to users.
      onQueueCreated?.(queueCreatedParams);
      toast.success('Outreach started');
    }
  };

  const dialogMessage =
    editingMessage === 'follow-up' ? outreach.followUpEmail : outreach.initialMessage;
  const dialogTitle =
    editingMessage === 'follow-up'
      ? 'Edit Follow-up Email'
      : isEmail
        ? 'Edit Initial Email'
        : 'Edit Initial DM';

  const handleDialogSave = (message: SavedListOutreachMessageDTO) => {
    if (editingMessage === 'follow-up') {
      outreach.setFollowUpEmail(message);
      return;
    }

    outreach.setInitialMessage(message);
  };

  const handleResizeStart = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    resizeStartXRef.current = event.clientX;
    resizeStartWidthRef.current = panelWidth;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = resizeStartXRef.current - moveEvent.clientX;
      setPanelWidth(clampPanelWidth(resizeStartWidthRef.current + deltaX));
    };

    const handleMouseUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <aside
      className="bg-muted relative flex h-full shrink-0 flex-col shadow-[-2px_0px_4px_-1px_#00000026]"
      style={{ width: `${panelWidth}px` }}
    >
      <div
        className="absolute top-0 left-0 z-10 h-full w-3 -translate-x-1/2 cursor-col-resize"
        role="separator"
        aria-label="Resize outreach panel"
        aria-orientation="vertical"
        onMouseDown={handleResizeStart}
      >
        <div className="bg-border absolute top-1/2 left-1/2 h-10 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 transition-opacity hover:opacity-100" />
      </div>

      <div className="bg-secondary relative p-4">
        <div className="pointer-events-none absolute inset-x-0 bottom-0 border-b border-zinc-200" />
        <Tabs
          value={outreach.channel}
          onValueChange={(value) => outreach.setChannel(value as typeof outreach.channel)}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="email">
              <Mail className="size-4" />
              Email
            </TabsTrigger>
            <TabsTrigger value="tiktok_dm">
              <MessageCircle className="size-4" />
              DM
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="grid gap-4">
          <div className="grid gap-2 rounded-md border border-zinc-200 bg-white p-3">
            <InfoRow label="Template">
              <Select value={outreach.templateName} onValueChange={outreach.setTemplateName}>
                <SelectTrigger className="h-auto min-h-9 w-full whitespace-normal">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {outreach.templateOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </InfoRow>
            <InfoRow label={isEmail ? 'Email account' : 'TikTok account'}>
              {outreach.isConnected ? (
                <Badge variant="secondary">Connected</Badge>
              ) : (
                <Badge variant="outline">{isEmail ? 'Connect Gmail' : 'Connect TikTok'}</Badge>
              )}
            </InfoRow>
            <InfoRow label="Recipients">
              <span className="font-medium">{outreach.recipientLabel}</span>
            </InfoRow>
          </div>

          <MessagePreview
            title={isEmail ? 'Initial Email' : 'Initial DM'}
            description="Sent when outreach starts"
            subject={isEmail ? outreach.initialMessage.subject : undefined}
            body={outreach.initialMessage.body}
            onEdit={() => setEditingMessage('initial')}
          />

          {isEmail ? (
            <>
              <MessagePreview
                title="Follow-up"
                description="Sent 3 days after initial email"
                subject={outreach.followUpEmail.subject}
                body={outreach.followUpEmail.body}
                enabled={outreach.followUpEnabled}
                onEnabledChange={outreach.setFollowUpEnabled}
                onEdit={() => setEditingMessage('follow-up')}
              />
              <ToggleSection
                title="AI Replies"
                description="AI replies and negotiates with creators for you (max 2 replies)"
                enabled={outreach.aiRepliesEnabled}
                onEnabledChange={outreach.setAiRepliesEnabled}
              />
            </>
          ) : (
            <>
              <ComingSoonSection
                title="Follow-up"
                description="Automatic follow-up DMs will be available soon"
              />
              <ComingSoonSection
                title="AI Replies"
                description="AI-powered DM replies will be available soon"
              />
            </>
          )}
        </div>
      </div>

      <div className="border-t border-zinc-200 bg-zinc-100 p-4">
        <div className="flex flex-col gap-2">
          <Button
            className="h-12 w-full"
            disabled={!hasRecipients || createQueueMutation.isPending}
            onClick={() => void handlePrimaryAction()}
          >
            <ArrowUpRight className="size-4" />
            {createQueueMutation.isPending ? 'Starting outreach...' : ctaLabel}
          </Button>
          {showCampaignAction ? (
            <Button
              className="h-10 w-full"
              variant="outline"
              disabled={confirmedCreatorCount === 0}
              onClick={onCreateCampaign}
            >
              Create campaign with confirmed creators
              {confirmedCreatorCount > 0 ? ` (${confirmedCreatorCount})` : ''}
            </Button>
          ) : null}
        </div>
      </div>

      <OutreachMessageDialog
        open={editingMessage !== null}
        title={dialogTitle}
        message={dialogMessage}
        includeSubject={isEmail}
        onOpenChange={(open) => {
          if (!open) setEditingMessage(null);
        }}
        onSave={handleDialogSave}
      />
    </aside>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function clampPanelWidth(width: number) {
  return Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, width));
}

function MessagePreview({
  title,
  description,
  subject,
  body,
  enabled,
  onEnabledChange,
  onEdit,
}: {
  title: string;
  description: string;
  subject?: string;
  body: string;
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  onEdit: () => void;
}) {
  const hasToggle = enabled !== undefined && onEnabledChange !== undefined;

  return (
    <section className={cn('grid gap-3', enabled === false && 'opacity-60')}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-foreground text-sm font-semibold">{title}</h3>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        {hasToggle ? <Switch checked={enabled} onCheckedChange={onEnabledChange} /> : null}
      </div>
      <div className="grid gap-3 rounded-md border border-zinc-200 bg-white p-3 text-sm shadow-sm">
        {subject ? (
          <div>
            <p className="text-muted-foreground mb-1 text-xs uppercase">Subject</p>
            <p className="line-clamp-2">{subject}</p>
          </div>
        ) : null}
        <div>
          <p className="text-muted-foreground mb-1 text-xs uppercase">Body</p>
          <p className="line-clamp-3 whitespace-pre-line">{body}</p>
        </div>
        <Button
          type="button"
          variant="link"
          className="text-primary h-auto justify-start p-0"
          disabled={enabled === false}
          onClick={onEdit}
        >
          Edit
        </Button>
      </div>
    </section>
  );
}

function ToggleSection({
  title,
  description,
  enabled,
  onEnabledChange,
}: {
  title: string;
  description: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
}) {
  return (
    <section className="flex items-start justify-between gap-3 rounded-md border border-zinc-200 bg-white p-3 shadow-sm">
      <div>
        <h3 className="text-foreground text-sm font-semibold">{title}</h3>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      <Switch checked={enabled} onCheckedChange={onEnabledChange} />
    </section>
  );
}

function ComingSoonSection({ title, description }: { title: string; description: string }) {
  return (
    <section className="grid gap-2 rounded-md border border-zinc-200 bg-white p-3 opacity-60 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-foreground text-sm font-semibold">{title}</h3>
        <Badge variant="outline">Coming Soon</Badge>
      </div>
      <p className="text-muted-foreground text-sm">{description}</p>
    </section>
  );
}
